import { useState, useEffect } from "react";
import { EntityCustomViewParams } from "@firecms/core";
import { User } from "../collections/users";
import { BoosterPackType, BoosterPacks, DEFAULT_BOOSTER_PACKS } from "./UserActionsView.types";
import { StatusCard } from "./StatusCard";
import { BoosterPackSection } from "./BoosterPackSection";
import { SubscriptionSection } from "./SubscriptionSection";
import { useAdminApi } from "../hooks/useAdminApi";
import type { components } from "../types/api.generated";

type ConfigResponse = components["schemas"]["ConfigResponse"];
type UserActionResponse = components["schemas"]["UserActionResponse"];

export function UserActionsView({ entity, modifiedValues }: EntityCustomViewParams<User>) {
    const { fetchApi } = useAdminApi();
    const [boosterPacks, setBoosterPacks] = useState<BoosterPacks>(DEFAULT_BOOSTER_PACKS);
    const [selectedBooster, setSelectedBooster] = useState<BoosterPackType>("starter");
    const [applying, setApplying] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [loading, setLoading] = useState(true);

    const user = modifiedValues || entity?.values;

    useEffect(() => {
        loadBoosterPackConfig();
    }, []);

    async function loadBoosterPackConfig() {
        try {
            const data = await fetchApi("/admin/api/config") as ConfigResponse;
            if (data.booster_packs) {
                // @ts-ignore - Mapping dynamic schema to local type
                setBoosterPacks({ ...DEFAULT_BOOSTER_PACKS, ...data.booster_packs });
            }
        } catch (e) {
            console.error("Failed to load booster pack config via API:", e);
        } finally {
            setLoading(false);
        }
    }

    async function applyBoosterPack() {
        if (!entity?.id) return;

        setApplying(true);
        setMessage(null);

        try {
            const result = await fetchApi(`/admin/api/users/${entity.id}/apply-booster`, {
                method: "POST",
                body: JSON.stringify({ pack_id: selectedBooster }),
            }) as UserActionResponse;

            setMessage({
                type: "success",
                text: result.message || `Successfully applied ${selectedBooster.replace("_", " ")} to user!`,
            });

            // Reload the page after a short delay to show updated values
            setTimeout(() => window.location.reload(), 1500);
        } catch (e: any) {
            console.error("Failed to apply booster pack:", e);
            setMessage({
                type: "error",
                text: e.message || "Failed to apply booster pack via server.",
            });
        } finally {
            setApplying(false);
        }
    }

    async function updateSubscription(tier: "free" | "pro", durationDays: number = 30) {
        if (!entity?.id) return;

        setApplying(true);
        setMessage(null);

        try {
            const result = await fetchApi(`/admin/api/users/${entity.id}/subscribe`, {
                method: "POST",
                body: JSON.stringify({
                    tier,
                    duration_days: durationDays
                }),
            }) as UserActionResponse;

            setMessage({
                type: "success",
                text: result.message || `Successfully updated subscription to ${tier}!`,
            });

            setTimeout(() => window.location.reload(), 1500);
        } catch (e: any) {
            console.error("Failed to update subscription:", e);
            setMessage({
                type: "error",
                text: e.message || "Failed to update subscription via server.",
            });
        } finally {
            setApplying(false);
        }
    }

    if (!user) {
        return <div className="p-4 text-gray-400">No user data available</div>;
    }

    return (
        <div className="p-6 space-y-8 bg-gray-950 min-h-full text-slate-200">
            {/* Current Status */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <span>👤</span> Current Status
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatusCard
                        label="Subscription"
                        value={user.subscription_tier || "free"}
                        badge
                        badgeColor={
                            user.subscription_tier === "pro" ? "blue" : "gray"
                        }
                    />
                    <StatusCard label="Text Credits" value={user.bonus_text_credits || 0} />
                    <StatusCard label="Voice Credits" value={user.bonus_voice_credits || 0} />
                    <StatusCard
                        label="Weekly Pass"
                        value={
                            user.weekly_pass_expires_at
                                ? `${user.weekly_pass_voice_remaining || 0} remaining`
                                : "None"
                        }
                    />
                </div>
            </div>

            {/* Message Display */}
            {message && (
                <div
                    className={`p-4 rounded-xl border ${message.type === "success"
                        ? "bg-green-500/10 border-green-500/30 text-green-400"
                        : "bg-red-500/10 border-red-500/30 text-red-400"
                        }`}
                >
                    {message.text}
                </div>
            )}

            <BoosterPackSection
                loading={loading}
                boosterPacks={boosterPacks}
                selectedBooster={selectedBooster}
                setSelectedBooster={setSelectedBooster}
                applyBoosterPack={applyBoosterPack}
                applying={applying}
            />

            <SubscriptionSection
                user={user}
                applying={applying}
                updateSubscription={updateSubscription}
            />
        </div>
    );
}
